/**
 * avatars/items/eyewear.js
 * Xây dựng hình học 3D cho createEyewear
 */

import * as THREE from 'three';

export function createEyewear(lib, type, headSize) {
  const eyewearGroup = new THREE.Group();
  eyewearGroup.name = `Eyewear_${type}`;

  // Tâm mắt 3D thực tế trên mặt nhân vật nằm ở ~0.425 * headSize
  const eyeY = headSize * 0.425;
  const eyeZ = headSize * 0.515; // Áp sát mặt Layer 2

  if (type === 'kinh_can') {
    // KÍNH CẬN TRI THỨC: Gọng đen thanh mảnh bao kín toàn bộ mắt từ trên xuống dưới
    const frameMat = new THREE.MeshStandardMaterial({ color: '#1e293b', roughness: 0.3 });
    const glassMat = new THREE.MeshStandardMaterial({
      color: '#f0f9ff',
      roughness: 0.1,
      metalness: 0.5,
      transparent: true,
      opacity: 0.4
    });
    lib.disposables.push(frameMat, glassMat);

    [-1, 1].forEach((side) => {
      // Viền gọng trên & dưới (Kích thước mở rộng bao phủ trọn mắt từ 0.30 đến 0.55 * headSize)
      const horizBarGeo = new THREE.BoxGeometry(headSize * 0.32, 0.02, 0.015);
      const topBar = new THREE.Mesh(horizBarGeo, frameMat);
      topBar.position.set(side * headSize * 0.22, eyeY + headSize * 0.12, eyeZ);
      eyewearGroup.add(topBar);

      const botBar = new THREE.Mesh(horizBarGeo, frameMat);
      botBar.position.set(side * headSize * 0.22, eyeY - headSize * 0.12, eyeZ);
      eyewearGroup.add(botBar);

      // Viền gọng trái & phải
      const vertBarGeo = new THREE.BoxGeometry(0.02, headSize * 0.24, 0.015);
      const leftBar = new THREE.Mesh(vertBarGeo, frameMat);
      leftBar.position.set(side * headSize * 0.22 - headSize * 0.15, eyeY, eyeZ);
      eyewearGroup.add(leftBar);

      const rightBar = new THREE.Mesh(vertBarGeo, frameMat);
      rightBar.position.set(side * headSize * 0.22 + headSize * 0.15, eyeY, eyeZ);
      eyewearGroup.add(rightBar);

      // Tròng kính trong suốt bao phủ kín mí mắt
      const lensGeo = new THREE.BoxGeometry(headSize * 0.28, headSize * 0.22, 0.01);
      const lens = new THREE.Mesh(lensGeo, glassMat);
      lens.position.set(side * headSize * 0.22, eyeY, eyeZ);
      eyewearGroup.add(lens);

      // Càng kính chạy dọc mang tai
      const templeGeo = new THREE.BoxGeometry(0.015, 0.015, headSize * 0.52);
      const temple = new THREE.Mesh(templeGeo, frameMat);
      temple.position.set(side * (headSize * 0.51), eyeY + headSize * 0.06, headSize * 0.24);
      eyewearGroup.add(temple);

      lib.disposables.push(horizBarGeo, vertBarGeo, lensGeo, templeGeo);
    });

    // Cầu kính ngang sống mũi
    const bridgeGeo = new THREE.BoxGeometry(headSize * 0.14, 0.02, 0.015);
    const bridge = new THREE.Mesh(bridgeGeo, frameMat);
    bridge.position.set(0, eyeY + headSize * 0.04, eyeZ);
    eyewearGroup.add(bridge);
    lib.disposables.push(bridgeGeo);
  } else if (type === 'kinh_den_ngau') {
    // KÍNH RÂM ĐEN COOL NGẦU: Tròng kính đen bóng phản quang trùm kín mắt
    const darkFrameMat = new THREE.MeshStandardMaterial({ color: '#09090b', roughness: 0.2 });
    const reflectiveLensMat = new THREE.MeshStandardMaterial({
      color: '#18181b',
      metalness: 0.92,
      roughness: 0.12
    });
    lib.disposables.push(darkFrameMat, reflectiveLensMat);

    // Cầu kính và khung gọng trên vắt ngang trán
    const topBrowGeo = new THREE.BoxGeometry(headSize * 0.9, 0.03, 0.02);
    const topBrow = new THREE.Mesh(topBrowGeo, darkFrameMat);
    topBrow.position.set(0, eyeY + headSize * 0.12, eyeZ);
    eyewearGroup.add(topBrow);

    [-1, 1].forEach((side) => {
      // Tròng kính bóng bao trọn mắt
      const darkLensGeo = new THREE.BoxGeometry(headSize * 0.35, headSize * 0.24, 0.015);
      const darkLens = new THREE.Mesh(darkLensGeo, reflectiveLensMat);
      darkLens.position.set(side * headSize * 0.22, eyeY, eyeZ);
      eyewearGroup.add(darkLens);

      // Càng kính dọc tai
      const templeGeo = new THREE.BoxGeometry(0.02, 0.02, headSize * 0.52);
      const temple = new THREE.Mesh(templeGeo, darkFrameMat);
      temple.position.set(side * (headSize * 0.51), eyeY + headSize * 0.06, headSize * 0.24);
      eyewearGroup.add(temple);

      lib.disposables.push(darkLensGeo, templeGeo);
    });

    lib.disposables.push(topBrowGeo);
  } else if (type === 'kinh_phi_cong') {
    // KÍNH PHI CÔNG AVIATOR: Dáng tròng giọt nước (Teardrop) mạ vàng sang trọng, tráng gương bạc
    const goldMat = new THREE.MeshStandardMaterial({
      color: '#f59e0b',
      metalness: 0.92,
      roughness: 0.25
    });
    const mirrorLensMat = new THREE.MeshStandardMaterial({
      color: '#1e293b',
      metalness: 0.95,
      roughness: 0.1
    });
    lib.disposables.push(goldMat, mirrorLensMat);

    // Thanh gọng ngang kép trên sống mũi (Double Brow Bar kinh điển)
    const topBarGeo = new THREE.BoxGeometry(headSize * 0.86, 0.018, 0.015);
    const topBar = new THREE.Mesh(topBarGeo, goldMat);
    topBar.position.set(0, eyeY + headSize * 0.12, eyeZ);
    eyewearGroup.add(topBar);

    const midBarGeo = new THREE.BoxGeometry(headSize * 0.32, 0.015, 0.015);
    const midBar = new THREE.Mesh(midBarGeo, goldMat);
    midBar.position.set(0, eyeY + headSize * 0.07, eyeZ);
    eyewearGroup.add(midBar);

    [-1, 1].forEach((side) => {
      // Phần trên tròng kính (hình chữ nhật rộng)
      const upperLensGeo = new THREE.BoxGeometry(headSize * 0.32, headSize * 0.14, 0.014);
      const upperLens = new THREE.Mesh(upperLensGeo, mirrorLensMat);
      upperLens.position.set(side * headSize * 0.22, eyeY + headSize * 0.04, eyeZ);
      eyewearGroup.add(upperLens);

      // Phần dưới vát thon giọt nước (đặc trưng Aviator)
      const lowerLensGeo = new THREE.BoxGeometry(headSize * 0.24, headSize * 0.11, 0.014);
      const lowerLens = new THREE.Mesh(lowerLensGeo, mirrorLensMat);
      lowerLens.position.set(side * headSize * 0.2, eyeY - headSize * 0.075, eyeZ);
      eyewearGroup.add(lowerLens);

      // Viền gọng vàng bao ngoài
      const outerRimGeo = new THREE.BoxGeometry(0.015, headSize * 0.24, 0.016);
      const outerRim = new THREE.Mesh(outerRimGeo, goldMat);
      outerRim.position.set(side * (headSize * 0.22 + headSize * 0.155), eyeY - headSize * 0.01, eyeZ);
      eyewearGroup.add(outerRim);

      // Càng kính vàng thanh mảnh
      const templeGeo = new THREE.BoxGeometry(0.015, 0.015, headSize * 0.52);
      const temple = new THREE.Mesh(templeGeo, goldMat);
      temple.position.set(side * (headSize * 0.51), eyeY + headSize * 0.06, headSize * 0.24);
      eyewearGroup.add(temple);

      lib.disposables.push(upperLensGeo, lowerLensGeo, outerRimGeo, templeGeo);
    });

    lib.disposables.push(topBarGeo, midBarGeo);
  } else if (type === 'kinh_nerd') {
    // Kính nerd: Tròng kính dày với hoa văn xoáy tròn ngộ nghĩnh trùm kín mắt
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, 128, 128);
    ctx.strokeStyle = '#0284c7';
    ctx.lineWidth = 8;
    for (let r = 12; r < 58; r += 14) {
      ctx.beginPath();
      ctx.arc(64, 64, r, 0, Math.PI * 2);
      ctx.stroke();
    }

    const spiralTex = new THREE.CanvasTexture(canvas);
    spiralTex.colorSpace = THREE.SRGBColorSpace;
    const spiralMat = new THREE.MeshBasicMaterial({ map: spiralTex });
    const frameMat = new THREE.MeshStandardMaterial({ color: '#09090b', roughness: 0.4 });
    lib.disposables.push(spiralTex, spiralMat, frameMat);

    [-1, 1].forEach((side) => {
      const frameGeo = new THREE.BoxGeometry(headSize * 0.35, headSize * 0.25, 0.02);
      const frame = new THREE.Mesh(frameGeo, frameMat);
      frame.position.set(side * headSize * 0.22, eyeY, eyeZ);
      eyewearGroup.add(frame);

      const spiralGeo = new THREE.PlaneGeometry(headSize * 0.29, headSize * 0.2);
      const spiral = new THREE.Mesh(spiralGeo, spiralMat);
      spiral.position.set(side * headSize * 0.22, eyeY, eyeZ + 0.012);
      eyewearGroup.add(spiral);
      lib.disposables.push(frameGeo, spiralGeo);
    });

    const bridgeGeo = new THREE.BoxGeometry(headSize * 0.12, 0.03, 0.02);
    const bridge = new THREE.Mesh(bridgeGeo, frameMat);
    bridge.position.set(0, eyeY + 0.02, eyeZ);
    eyewearGroup.add(bridge);
    lib.disposables.push(bridgeGeo);
  }

  return eyewearGroup;
}
